import React, {ChangeEvent, Component, FormEvent} from "react";
import {MenuItem, Select, TextField} from '@material-ui/core';
import {ValidatedFieldProps, ValidatedFieldState} from 'components/forms/validation';
import {Country} from 'services/entities/country';
import './index.scss';
import {loadCountries} from 'services/i18n';
import config from 'config';

export interface PhoneNumberFieldProps extends ValidatedFieldProps {
  disabled?: boolean;
  name?: string;
  label?: string;
  required?: boolean;
  onInput?: (ev:{value: string})=>void;
  className?: string;
}

interface PhoneNumberFieldState extends ValidatedFieldState {
  countryCode: string,
  number: string,
  countries: Country[]|null
}

export default class PhoneNumberField extends Component<PhoneNumberFieldProps, PhoneNumberFieldState> {
  private static MAX_NUMBER_LENGTH = 30;

  state: PhoneNumberFieldState = {
    dirty: false,
    touched: false,
    validationMessage: undefined,
    countryCode: config.countries.defaultCountry,
    number: '',
    countries: null
  };

  componentDidMount(): void {
    loadCountries().then(
      countries => {
        this.setState({'countries': countries});
      }
    );
    if(this.props.validationGroup && this.props.name) {
      this.props.validationGroup.addFieldListener(this.props.name, this.onValidChanged);
    }
  }

  componentWillUnmount(): void {
    if(this.props.validationGroup && this.props.name) {
      this.props.validationGroup.removeFieldListener(this.props.name, this.onValidChanged);
    }
  }

  onValidChanged = (errors: string[], forceDirty: boolean) => {
    const touched = this.state.touched || forceDirty;
    const dirty = this.state.dirty || forceDirty;
    if(errors) {
      let msg = this.props.validationMessages ? this.props.validationMessages[errors[0]] : null;
      if(!msg) {
        msg = errors[0];
      }
      this.setState({ validationMessage: msg, dirty: dirty, touched: touched });
    } else {
      this.setState({ validationMessage: undefined, dirty: dirty, touched: touched });
    }
  };

  onInput = (ev: FormEvent<HTMLInputElement>) => {
    ev.preventDefault();
    let value = (ev.target as HTMLInputElement).value;
    if(value) {
      value = value.replace(/[^\d +#()-]/, '');
      if(value.length > PhoneNumberField.MAX_NUMBER_LENGTH) {
        value = value.substring(0, PhoneNumberField.MAX_NUMBER_LENGTH);
      }
    }
    this.setState({ number: value }, () => this.onChange());
  };

  onCountryCodeChange = (ev: ChangeEvent<{ name?: string; value: unknown }>) => {
    this.setState({ 'countryCode': ev.target.value as string }, () => this.onChange());
  };

  onBlur = () => {
    this.setState({touched: true});
  };

  private onChange() {
    const countries = this.state.countries;
    const countryCode = this.state.countryCode;
    const country = countries && countryCode ? countries.find(c => c.code === countryCode) : null;
    let value = '';
    if(country && country.callingCodes && this.state.number) {
      value = `${country.callingCodes[0]} ${this.state.number}`;
    } else {
      value = this.state.number;
    }
    this.setState({dirty: true}, () => {
      if(this.props.model && this.props.name) {
        this.props.model[this.props.name] = value;
      }
      if(this.props.validationGroup && this.props.name) {
        this.props.validationGroup.validateField(this.props.name);
      }
    });
    if(this.props.onInput) {
      this.props.onInput({ value: value });
    }
  }

  render() {
    const showError = this.state.touched && this.state.dirty && !!this.state.validationMessage;
    const fullClassName = this.props.className ? `phone-number-field ${this.props.className}` : 'phone-number-field';
    const countries = this.state.countries;
    return <div className={fullClassName}>
      { countries && <Select variant="outlined" className="country-code" onChange={this.onCountryCodeChange}
        onBlur={this.onBlur} disabled={this.props.disabled} value={this.state.countryCode}>
        { countries.map(c => <MenuItem key={c.code} value={c.code}><img src={c.flag} alt={c.name} /></MenuItem>)}
      </Select> }
      <TextField variant="outlined" className="number" onInput={this.onInput} disabled={this.props.disabled}
                          required={this.props.required} label={this.props.label} type="tel"
                          onBlur={this.onBlur} error={showError} value={this.state.number}
                          helperText={showError ? this.state.validationMessage : undefined} />
    </div>;
  }
}